import { slotKey, type FileSlot, type FileUploads } from '../files/fileUploads';

/**
 * Per upload module, the stored ids each answer and its slot last agreed on, by `slotKey` (ENG-9090, ENG-9091).
 * `useSlotFiles` keeps them here rather than in the item's own setup, because the field list is virtualized and the
 * matrix redraws its cells when a row is added or removed: an item unmounts, and on mount it has to tell which of the
 * two moved while it was gone. Keyed by the module, so the record goes with the entry it was made for.
 */
const agreedIds = new WeakMap<FileUploads, Map<string, string[]>>();

export const agreedFileIds = (uploads: FileUploads) => {
  if (!agreedIds.has(uploads)) {
    agreedIds.set(uploads, new Map());
  }
  return agreedIds.get(uploads)!;
};

const sameIds = (one: readonly string[], other: readonly string[]) =>
  one.length === other.length && one.every((identifier, index) => identifier === other[index]);

/**
 * Brings a slot and the answer `answered` back into step (ENG-9040 decision 13). When the slot moved since the two last
 * agreed, because a file stored or the reader deleted one, it hands the slot's answer to `report` and records that the
 * two agree on it; a caller that may not write, a read-only item, passes no `report`, and nothing moves. When the
 * answer let go of a file, as a Show effect's hide, a cleared dependent or a reused matrix row id does, the slot lets go
 * of it too, so the file doesn't come back. An answer of `undefined` is one nobody has written since a hide or a
 * cleared dependent emptied it, or a new matrix row's cell, which the item's own report never leaves, so the slot's
 * removed files go as well: they were deleted from an answer that is gone, and an Undo would put one into an answer
 * the reader never deleted from (ENG-9096). Before the two have ever agreed under a module, the slot has not been read
 * into yet by `readStored`, and nothing moves. `useSlotFiles` runs it on every move, and Save runs it for every slot,
 * for an item the virtualized list unmounted.
 */
export const syncSlot = (
  uploads: FileUploads,
  slot: FileSlot,
  answered: readonly string[] | undefined,
  report?: (fileIds: string[]) => void
) => {
  if (answered === undefined) {
    uploads.files(slot).filter(file => file.state === 'removed').forEach(file => uploads.letGo(file.localId));
  }
  const held = answered ?? [];
  const key = slotKey(slot);
  const answer = uploads.answer(slot);
  const agreed = agreedFileIds(uploads);
  if (sameIds(answer, held)) {
    agreed.set(key, answer);
    return;
  }
  const known = agreed.get(key);
  if (!known) {
    return;
  }
  const letGo = known.filter(identifier => !held.includes(identifier) && answer.includes(identifier));
  if (letGo.length) {
    // Let go rather than removed: nobody deleted these, so there is nothing for an undo to put back.
    uploads.files(slot).filter(file => file.state === 'stored' && letGo.includes(file.blob.identifier))
      .forEach(file => uploads.letGo(file.localId));
    return;
  }
  if (report) {
    report(answer);
    agreed.set(key, answer);
  }
};
